import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, getAll } from '../api/client';
import { Modal, FormError, ConfirmDialog } from './admin-ui';

/**
 * Slice 7.8 — Data Items panel.
 *
 * Shows the current asset's `contains_data` children — the data items
 * (databases, file stores, or any asset) it holds. Provides add/remove
 * controls. Visible on every asset (per the "any asset can hold data"
 * decision in Slice 7.8 design), so even a hardware switch shows the
 * affordance.
 *
 * Add flow: opens a modal with a search-filtered list of candidate
 * assets in the same company. Selecting one creates a contains_data
 * edge. The endpoint rejects cycles for contains_data and the panel
 * surfaces a friendly error.
 *
 * Remove flow: a delete-confirmation dialog removes the edge but does
 * NOT delete the data asset itself — the asset still exists, it just
 * no longer rolls into THIS asset's Data Risk score.
 */

interface DataChild {
  id: string;  // relationship id
  parent_asset_id: string;
  child_asset_id: string;
  relationship_type: string;
  child_asset_name: string;
  child_asset_type_id: string;
  child_asset_type_name: string;
  child_asset_type_family: string;
  created_at: string;
}

interface AssetSummary {
  id: string;
  name: string;
  asset_type_id: string;
  asset_type_name?: string;
}

export function DataItemsPanel({ assetId, companyId }: {
  assetId: string;
  companyId: string;
}) {
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<DataChild | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const childrenQ = useQuery({
    queryKey: ['asset', assetId, 'data-children'],
    queryFn: () => api.get<{ items: DataChild[]; total: number }>(
      `/assets/${assetId}/relationships?direction=children&type=contains_data`,
    ),
  });

  async function doRemove() {
    if (!confirmRemove) return;
    setRemoveError(null);
    try {
      await api.delete(`/assets/${assetId}/relationships/${confirmRemove.id}`);
      qc.invalidateQueries({ queryKey: ['asset', assetId, 'data-children'] });
      setConfirmRemove(null);
    } catch (err) {
      setRemoveError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err));
    }
  }

  const items = childrenQ.data?.items ?? [];

  return (
    <>
      <div className="border-t border-ink-100 pt-6 mt-6">
        <div className="flex items-baseline justify-between mb-2">
          <h3 className="text-sm font-medium text-ink-900">Data Items</h3>
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="text-xs text-accent hover:underline"
          >
            + Add data item
          </button>
        </div>
        <p className="text-xs text-ink-500 mb-3">
          Data assets held by this asset. Their Data Risk scores roll up
          into this asset's overall Data Risk via max-of aggregation.
        </p>

        {childrenQ.isLoading ? (
          <div className="text-xs text-ink-400 italic">Loading…</div>
        ) : childrenQ.isError ? (
          <div className="text-xs text-red-700">
            Failed to load: {(childrenQ.error as Error)?.message ?? 'unknown error'}
          </div>
        ) : items.length === 0 ? (
          <div className="text-xs text-ink-400 italic px-3 py-3 border border-dashed border-ink-200 rounded-sm bg-ink-50/30">
            No data items linked. Click "+ Add data item" to associate
            existing data assets with this one.
          </div>
        ) : (
          <div className="border border-ink-200 rounded-sm overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-ink-50 border-b border-ink-200">
                <tr className="text-left text-ink-500 uppercase tracking-wide text-[10px]">
                  <th className="px-3 py-2">Data item</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">Family</th>
                  <th className="px-3 py-2 w-20">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c.id} className="border-t border-ink-100 hover:bg-ink-50/40">
                    <td className="px-3 py-2 text-ink-700 font-medium">{c.child_asset_name}</td>
                    <td className="px-3 py-2 text-ink-700">{c.child_asset_type_name}</td>
                    <td className="px-3 py-2 text-ink-500">{c.child_asset_type_family}</td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() => setConfirmRemove(c)}
                        className="text-[11px] text-red-700 hover:underline"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AddDataItemModal
        open={showAdd}
        onClose={() => setShowAdd(false)}
        parentAssetId={assetId}
        companyId={companyId}
        existingChildIds={items.map((c) => c.child_asset_id)}
      />

      <ConfirmDialog
        open={!!confirmRemove}
        title="Remove data item"
        message={
          removeError
            ? `Failed to remove: ${removeError}`
            : `This removes the link between this asset and "${confirmRemove?.child_asset_name}". The data asset itself is not deleted; it just no longer rolls up into this asset's Data Risk.`
        }
        confirmLabel="Remove"
        danger
        onConfirm={doRemove}
        onCancel={() => { setConfirmRemove(null); setRemoveError(null); }}
      />
    </>
  );
}

function AddDataItemModal({ open, onClose, parentAssetId, companyId, existingChildIds }: {
  open: boolean;
  onClose: () => void;
  parentAssetId: string;
  companyId: string;
  existingChildIds: string[];
}) {
  const qc = useQueryClient();
  const [filter, setFilter] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load all assets in the same company. The user picks one to link as
  // a child. We could narrow to data.* asset types, but per the design
  // any asset can be a child — so show them all and let the user judge.
  //
  // Note: api.get unwraps the {data, meta} envelope returned by the
  // /assets list endpoint to a flat AssetSummary[] array. Earlier code
  // expected {items: [...]} which silently produced an empty list.
  // The endpoint caps a page at 200, so getAll reads every page.
  const candidatesQ = useQuery({
    queryKey: ['assets-by-company', companyId],
    queryFn: () => getAll<AssetSummary>(`/assets?company_id=${encodeURIComponent(companyId)}`),
    enabled: open,
  });

  // Filter: hide self, hide already-linked children
  const candidates = (candidatesQ.data ?? []).filter((a) => {
    if (a.id === parentAssetId) return false;
    if (existingChildIds.includes(a.id)) return false;
    if (filter.trim() && !a.name.toLowerCase().includes(filter.trim().toLowerCase())) return false;
    return true;
  });

  async function handlePick(childAssetId: string) {
    setSubmitting(true);
    setError(null);
    try {
      await api.post(`/assets/${parentAssetId}/relationships`, {
        child_asset_id: childAssetId,
        relationship_type: 'contains_data',
      });
      qc.invalidateQueries({ queryKey: ['asset', parentAssetId, 'data-children'] });
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Add data item" widthClass="max-w-2xl w-full">
      <FormError message={error} />
      <p className="text-xs text-ink-500 mb-3">
        Pick an existing asset to link as a data child. Cycles in the
        contains_data hierarchy are blocked automatically.
      </p>
      <input
        type="text"
        placeholder="Filter by name..."
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        className="border border-ink-200 rounded-sm px-3 py-1 text-sm w-full mb-3 focus:outline-none focus:border-accent"
      />
      <div className="border border-ink-200 rounded-sm bg-ink-50/30 max-h-[360px] overflow-y-auto">
        {candidatesQ.isLoading ? (
          <div className="text-xs text-ink-400 italic px-3 py-3">Loading…</div>
        ) : candidates.length === 0 ? (
          <div className="text-xs text-ink-400 italic px-3 py-3">
            {filter.trim() ? 'No assets match the filter.' : 'No eligible assets to link.'}
          </div>
        ) : (
          candidates.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => handlePick(a.id)}
              disabled={submitting}
              className="block w-full text-left px-3 py-2 text-xs hover:bg-white border-b border-ink-100 last:border-b-0 disabled:opacity-50"
            >
              <div className="text-ink-700 font-medium">{a.name}</div>
              {a.asset_type_name && (
                <div className="text-ink-400 text-[10px]">{a.asset_type_name}</div>
              )}
            </button>
          ))
        )}
      </div>
      <div className="flex justify-end mt-4">
        <button
          type="button"
          onClick={onClose}
          className="text-sm px-4 py-1 border border-ink-200 rounded-sm hover:bg-ink-50"
          disabled={submitting}
        >
          Cancel
        </button>
      </div>
    </Modal>
  );
}
